// Shared prev/next pager for a client-side paged table, and the slice that feeds it.
//
// This lived privately inside the Tokens view until WB6 needed the same thing on the Streams page.
// It is two small functions, and the reason to share them rather than copy them is the CLAMP: a
// page index that outlives the rows it indexed (delete the last grant on page 3, narrow a filter
// down to four routes) must land on the last real page rather than render an empty table that
// looks like "nothing matches". Getting that right twice, independently, is how two tables come to
// disagree about what an out-of-range page means.

/** Slice `rows` to the current page, clamping the page into range. */
export function pageSlice<T>(
  rows: T[],
  page: number,
  size: number,
): { rows: T[]; page: number; pageCount: number } {
  const pageCount = Math.max(1, Math.ceil(rows.length / size));
  const clamped = Math.min(Math.max(0, page), pageCount - 1);
  return { rows: rows.slice(clamped * size, clamped * size + size), page: clamped, pageCount };
}

/** Prev/next pager. Renders nothing when everything fits on one page. */
export function Pager({
  page,
  pageCount,
  total,
  onPage,
  label,
}: {
  page: number;
  pageCount: number;
  total: number;
  onPage: (p: number) => void;
  /** Distinguishes two pagers on one screen for a screen reader. */
  label?: string;
}) {
  if (pageCount <= 1) return null;
  return (
    <div className="pager">
      <button
        className="btn btn-sm"
        disabled={page <= 0}
        aria-label={label ? `${label}: previous page` : 'Previous page'}
        onClick={() => onPage(page - 1)}
      >
        Prev
      </button>
      <span className="muted small">
        Page {page + 1} / {pageCount} · {total} total
      </span>
      <button
        className="btn btn-sm"
        disabled={page >= pageCount - 1}
        aria-label={label ? `${label}: next page` : 'Next page'}
        onClick={() => onPage(page + 1)}
      >
        Next
      </button>
    </div>
  );
}
